// ThemeSettings/index.js - Updated version

import React, { useEffect, useState } from "react";
import AlertPopup from "../../../AlertPopup";
import LoaderComp from "../../../LoaderComp";
import styles from "../LeadGenBotSettings/BotSettings.module.css";
import LeadGenBotSettingStyle from "./LiveAgentBotSettings.module.css";
import ThemeCustomizer from "../../../NewCreateAppF/ThemeCustomizer";
import Modal from "../../../Modal";
import modalContentSty from "../LeadGenBotSettings/ModalContent.module.css";
import ChatModalOpenerPicker from "../../../NewCreateAppF/ChatModalOpenerPicker";

const backend_api_base_url = `${process.env.REACT_APP_SL_BE_BASE_ENV_PART}/${process.env.REACT_APP_STAGE_ENV}`;

const getToken = () => localStorage.getItem("token");

const CORE_KEYS = [
  "app_name",
  "ai_agent_bot_enabled",
  "dont_show_chat_box_at_logout",
  "exclude_paths",
  "live_agent_enabled",
  "allow_nested",
  "app_id",
  "auth_key",
  "chat_settings",
  "enabled_bot_content",
  "feature",
  "region",
  "role",
  "settings",
  "tenant_id",
  "timestamp",
  "tenant_info",
  "isTraining",
  "session_enabled",
  "live_settings",
];

export default function ThemeSettings({ appData, tenantPlan }) {
  console.log("appDataddishere?dappData", JSON.stringify(appData));
  const [initialData, setInitialData] = useState(null);
  const [botContent, setBotContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: "", message: "" });
  const [localChatSettings, setLocalChatSettings] = useState(
    appData.chat_settings,
  );
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingUpdatePayloads, setPendingUpdatePayloads] = useState({
    payload1: null,
  });
  const [selectedChatStyle, setSelectedChatStyle] = useState(() => {
    // Load saved style from chat_settings or default to 'default'
    return appData.chat_settings?.chat_opener_style || "default";
  });

  // Function to sync chat settings back to botContent
  const handleChatSettingsChange = (updatedChatSettings) => {
    console.log("Updating chat settings:", updatedChatSettings);
    setLocalChatSettings(updatedChatSettings);

    // Sync to botContent
    setBotContent((prev) => ({
      ...prev,
      chat_settings: updatedChatSettings,
    }));
  };

  // Handle chat style change - ONLY saves the style flag, no HTML
  const handleChatStyleChange = (styleKey) => {
    setSelectedChatStyle(styleKey);

    // Update chat_settings with ONLY the style flag
    const updatedChatSettings = {
      ...localChatSettings,
      chat_opener_style: styleKey,
      // NOT saving any HTML here
    };

    handleChatSettingsChange(updatedChatSettings);
  };

  const hasChanges = () => {
    if (!initialData || !botContent) return false;

    const coreChanged = CORE_KEYS.some((key) => {
      if (key === "chat_settings") return false;
      if (
        initialData.botContent[key] === undefined ||
        botContent[key] === undefined
      ) {
        return false;
      }
      return (
        JSON.stringify(initialData.botContent[key]) !==
        JSON.stringify(botContent[key])
      );
    });

    // Compare chat_settings from initialData and current botContent
    const chatSettingsChanged =
      JSON.stringify(initialData.chatSettings) !==
      JSON.stringify(botContent.chat_settings);

    return coreChanged || chatSettingsChanged;
  };

  const confirmUpdate = async (confirmed) => {
    setShowConfirmModal(false);

    if (!confirmed) {
      showAlert("info", "Update cancelled.");
      return;
    }

    const { payload1 } = pendingUpdatePayloads;

    if (!payload1) {
      showAlert("error", "Update data not found");
      return;
    }

    setUpdating(true);

    try {
      const region_prefix = appData.region.toLowerCase();
      const token = getToken();
      const updateAppResponse = await fetch(
        `https://${region_prefix}.apps2.${backend_api_base_url}/update_app?act_type=tenant&app_name=${appData.app_name}`,
        {
          method: "PUT",
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload1),
        },
      );

      const updateAppData = await updateAppResponse.json();

      if (!updateAppResponse.ok) {
        showAlert("error", "Failed to update Live Agent configuration");
        setUpdating(false);
        return;
      }

      // Update initial data to match current state
      setInitialData({
        botContent: { ...botContent },
        chatSettings: { ...botContent.chat_settings },
      });

      showAlert("success", "Live Agent settings updated successfully!");

      if (window.MyPackage?.processHardReset) {
        window.MyPackage.processHardReset(
          token,
          appData.app_name,
          appData.region.toLowerCase(),
        );
      }
    } catch (error) {
      console.error("Update failed:", error);
      showAlert("error", "Update failed: " + error.message);
    } finally {
      setUpdating(false);
      setPendingUpdatePayloads({ payload1: null });
    }
  };

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
  };

  // ========== VALIDATION FUNCTION FOR CHATBOT STATES ==========
  const checkOtherChatbotsEnabled = () => {
    // Check if AI Chat is enabled
    const isAiAgentEnabled = botContent?.ai_agent_bot_enabled === true;
    // Check if LeadGen Bot (QnA) is enabled
    const isLeadGenEnabled = botContent?.enabled_bot_content === true;

    return {
      aiAgentEnabled: isAiAgentEnabled,
      leadGenEnabled: isLeadGenEnabled,
      anyOtherEnabled: isAiAgentEnabled || isLeadGenEnabled,
    };
  };
  // ========== END VALIDATION FUNCTION ==========

  useEffect(() => {
    if (!appData?.tenant_id || !appData?.app_name) return;

    const initialBotContent = {
      ...appData,
      live_settings: appData.live_settings || {
        WELCOME_MSG: "",
        OFFLINE_MSG: "",
      },
    };

    // Initialize chat_settings with style if not present
    if (!initialBotContent.chat_settings?.chat_opener_style) {
      initialBotContent.chat_settings = {
        ...initialBotContent.chat_settings,
        chat_opener_style: "default",
      };
    }

    console.log(
      "Initialized bot content from appData for Live Agent:",
      initialBotContent,
    );
    setBotContent(initialBotContent);
    setInitialData({
      botContent: { ...initialBotContent },
      chatSettings: { ...initialBotContent.chat_settings },
    });
    setLocalChatSettings(appData.chat_settings);
    setSelectedChatStyle(
      initialBotContent.chat_settings?.chat_opener_style || "default",
    );
    setLoading(false);
  }, [appData]);

  const handleUpdate = async () => {
    if (!botContent) {
      showAlert("error", "No Live Agent content to update");
      return;
    }

    // ========== VALIDATION: Check if trying to disable all chatbots ==========
    const isLiveAgentEnabled = botContent.live_agent_enabled;
    const { aiAgentEnabled, leadGenEnabled, anyOtherEnabled } =
      checkOtherChatbotsEnabled();

    // If Live Agent is being disabled (or is already disabled) AND no other chatbots are enabled
    if (!isLiveAgentEnabled && !anyOtherEnabled) {
      showAlert(
        "error",
        "❌ Cannot disable Live Agent Bot! You need at least one chatbot enabled. Please enable either AI Chat or QnA (LeadGen) Bot before disabling Live Agent.",
      );
      return;
    }

    // Also check if we're about to save with Live Agent disabled and no other bots enabled
    // (This handles the case where Live Agent is currently enabled but we're about to disable it)
    const willBeDisabled = !isLiveAgentEnabled;
    if (willBeDisabled && !anyOtherEnabled) {
      showAlert(
        "error",
        "❌ Cannot disable Live Agent Bot! You need at least one chatbot enabled. Currently, both AI Chat and QnA Bot are disabled. Please enable one of them first.",
      );
      return;
    }
    // ========== END VALIDATION ==========

    const payload1 = {};
    Object.keys(botContent).forEach((key) => {
      if (CORE_KEYS.includes(key)) payload1[key] = botContent[key];
    });

    console.log("Live Agent Payload:", JSON.stringify(payload1, null, 2));
    console.log(
      "Chat opener style being saved:",
      payload1.chat_settings?.chat_opener_style,
    );

    setPendingUpdatePayloads({ payload1 });
    setShowConfirmModal(true);
  };

  const handleCloseAlert = () => {
    setAlert({ ...alert, show: false });
  };

  const handleModalClose = () => {
    setShowConfirmModal(false);
  };

  if (loading) {
    return (
      <div className={LeadGenBotSettingStyle.centered}>
        <LoaderComp message="Loading Live Agent Settings" />
      </div>
    );
  }

  if (!botContent) {
    return <div>Error: Unable to load Live Agent content</div>;
  }

  return (
    <div className={LeadGenBotSettingStyle.botContent_wrap}>
      {/* Chat Modal Opener Theme Selection Section — extracted to reusable picker */}
      <ChatModalOpenerPicker
        selectedStyle={selectedChatStyle}
        onStyleChange={handleChatStyleChange}
        styles={LeadGenBotSettingStyle}
      />

      <ThemeCustomizer
        chatSettings={localChatSettings}
        setChatSettings={handleChatSettingsChange}
      />

      <button
        onClick={handleUpdate}
        disabled={updating || !botContent || loading || !hasChanges()}
        className={`${LeadGenBotSettingStyle.updateBtn} ${
          updating ? LeadGenBotSettingStyle.disabled : ""
        }`}
      >
        {updating ? "Updating..." : "Update Theme Settings"}
      </button>

      {alert.show && (
        <AlertPopup
          type={alert.type}
          message={alert.message}
          onClose={handleCloseAlert}
        />
      )}

      <Modal
        isOpen={showConfirmModal}
        onClose={handleModalClose}
        hideCloseButton={true}
        title="Confirmation Required"
      >
        <div className={modalContentSty.modalContent}>
          <div className={modalContentSty.modalContentWrap}>
            <div className={modalContentSty.modalContentIc}>⚠️</div>
            <p className={modalContentSty.modalMessage}>
              All the clients would be rebooted! Do you want to continue?
            </p>
          </div>
          <div className={modalContentSty.modalActions}>
            <button
              className={`${modalContentSty.modalButton} ${modalContentSty.cancelBtn}`}
              onClick={() => confirmUpdate(false)}
            >
              Cancel
            </button>
            <button
              className={`${modalContentSty.modalButton} ${modalContentSty.okBtn}`}
              onClick={() => confirmUpdate(true)}
            >
              Ok
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}